/**
 * SecretsVaultPanel — grouped admin secrets management.
 *
 * Groups: AI Models · Email & Comms · Publishing & Print ·
 *         Content & Safety · Integrations
 *
 * Each card shows:
 *   ✦ A masked text input (save / clear buttons)
 *   ✦ DB-override badge (green = admin override active)
 *   ✦ Env-fallback badge (amber = using env var; grey = absent)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Save,
  Trash2,
  ShieldCheck,
  Server,
} from 'lucide-react';
import { adminApi } from '@/lib/api';

// ── Status pill ───────────────────────────────────────────────────────────
function StatusPill({ configured, envActive }) {
  if (configured) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
        <ShieldCheck className="h-2.5 w-2.5" />
        Admin override
      </span>
    );
  }
  if (envActive) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        <Server className="h-2.5 w-2.5" />
        Using env var
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
      <AlertCircle className="h-2.5 w-2.5" />
      Not configured
    </span>
  );
}

// ── Single secret card ────────────────────────────────────────────────────
function SecretCard({ title, description, cfgKey, envVar, placeholder, configured, envActive, onSaved }) {
  const [value, setValue]   = useState('');
  const [show, setShow]     = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await adminApi.saveConfig({ [cfgKey]: value.trim() });
      toast.success(`${title} saved.`);
      setValue('');
      onSaved();
    } catch { toast.error('Save failed.'); }
    finally  { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await adminApi.saveConfig({ [cfgKey]: '' });
      toast.success(`${title} cleared — falling back to env var.`);
      onSaved();
    } catch { toast.error('Clear failed.'); }
    finally  { setSaving(false); }
  };

  return (
    <div
      className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900"
      data-testid={`secret-card-${cfgKey}`}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{title}</p>
          {description && (
            <p className="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">{description}</p>
          )}
          {envVar && (
            <p className="mt-0.5 font-mono text-[10px] text-zinc-400 dark:text-zinc-500">
              Env var: <span className="text-zinc-600 dark:text-zinc-300">{envVar}</span>
            </p>
          )}
        </div>
        <StatusPill configured={configured} envActive={envActive} />
      </div>

      <div className="mt-3 flex gap-2">
        <div className="relative flex-1">
          <Input
            type={show ? 'text' : 'password'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={configured ? '••••••  replace current override' : (placeholder || 'Enter value…')}
            className="pr-8 font-mono text-xs"
            data-testid={`secret-input-${cfgKey}`}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            onClick={() => setShow((p) => !p)}
            tabIndex={-1}
          >
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          </button>
        </div>
        <Button
          size="sm"
          disabled={!value.trim() || saving}
          onClick={save}
          data-testid={`secret-save-${cfgKey}`}
        >
          <Save className="h-3.5 w-3.5" />
        </Button>
        {configured && (
          <Button
            size="sm"
            variant="ghost"
            disabled={saving}
            onClick={clear}
            className="text-zinc-400 hover:text-rose-500"
            data-testid={`secret-clear-${cfgKey}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Section wrapper ───────────────────────────────────────────────────────
function Section({ title, description, children }) {
  return (
    <section className="space-y-3">
      <div className="border-b border-zinc-200 pb-2 dark:border-zinc-700">
        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">{title}</h3>
        {description && (
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{description}</p>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────
export default function SecretsVaultPanel() {
  const [cfg, setCfg]       = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const data = await adminApi.config();
      setCfg(data);
    } catch { toast.error('Failed to load secrets config.'); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading) {
    return <p className="p-6 text-sm text-zinc-500">Loading secrets vault…</p>;
  }

  const c  = (k) => Boolean(cfg?.[`${k}_configured`]);
  const ea = (k) => Boolean(cfg?.[`${k}_env_active`]);

  return (
    <div className="space-y-8 p-6">
      <div>
        <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
          Secrets Vault
        </h2>
        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Admin overrides stored here take precedence over Emergent environment variables.
          When an override is cleared the env-var fallback (shown in amber) resumes automatically.
        </p>
      </div>

      {/* ── AI Models ──────────────────────────────────────────────── */}
      <Section
        title="AI Models"
        description="Platform API keys for AI generation services. Resolution: Admin override → env var → absent."
      >
        <SecretCard
          title="Anthropic Platform Key"
          description="Used for all Claude generations (Opus, Sonnet, Haiku). Overrides ANTHROPIC_API_KEY."
          cfgKey="anthropic_platform_key"
          envVar="ANTHROPIC_API_KEY"
          placeholder="sk-ant-…"
          configured={c('anthropic_platform_key')}
          envActive={ea('anthropic_platform_key')}
          onSaved={reload}
        />
        <SecretCard
          title="ElevenLabs API Key"
          description="Text-to-speech & audiobook voice synthesis."
          cfgKey="elevenlabs_api_key"
          envVar="ELEVENLABS_API_KEY"
          placeholder="sk_…"
          configured={c('elevenlabs_api_key')}
          envActive={ea('elevenlabs_api_key')}
          onSaved={reload}
        />
        <SecretCard
          title="Fal.ai Key"
          description="Emergent Managed image/video generation (Nano Banana, Wan, Kling)."
          cfgKey="fal_api_key"
          envVar="FAL_KEY"
          placeholder="…"
          configured={c('fal_api_key')}
          envActive={ea('fal_api_key')}
          onSaved={reload}
        />
      </Section>

      {/* ── Email & Comms ──────────────────────────────────────────── */}
      <Section
        title="Email & Comms"
        description="Transactional email sending (Resend) and internal notification webhooks."
      >
        <SecretCard
          title="Resend API Key"
          description="Outbound transactional email (welcome, receipts, password reset, etc.)."
          cfgKey="resend_api_key"
          envVar="RESEND_API_KEY"
          placeholder="re_…"
          configured={c('resend_api_key')}
          envActive={ea('resend_api_key')}
          onSaved={reload}
        />
        <SecretCard
          title="Slack Webhook URL"
          description="Internal ops alerts (new signups, payment events, errors)."
          cfgKey="slack_webhook_url"
          envVar="SLACK_WEBHOOK_URL"
          placeholder="https://hooks.slack.com/…"
          configured={c('slack_webhook_url')}
          envActive={ea('slack_webhook_url')}
          onSaved={reload}
        />
        <SecretCard
          title="Discord Webhook URL"
          description="Community & growth notification channel."
          cfgKey="discord_webhook_url"
          envVar="DISCORD_WEBHOOK_URL"
          placeholder="https://discord.com/api/webhooks/…"
          configured={c('discord_webhook_url')}
          envActive={ea('discord_webhook_url')}
          onSaved={reload}
        />
      </Section>

      {/* ── Publishing & Print ─────────────────────────────────────── */}
      <Section
        title="Publishing & Print"
        description="Lulu Direct print-on-demand integration."
      >
        <SecretCard
          title="Lulu Client Key"
          description="OAuth client key for Lulu Direct API."
          cfgKey="lulu_client_key"
          envVar="LULU_CLIENT_KEY"
          placeholder="…"
          configured={c('lulu_client_key')}
          envActive={ea('lulu_client_key')}
          onSaved={reload}
        />
        <SecretCard
          title="Lulu Client Secret"
          description="OAuth client secret for Lulu Direct API."
          cfgKey="lulu_client_secret"
          envVar="LULU_CLIENT_SECRET"
          placeholder="…"
          configured={c('lulu_client_secret')}
          envActive={ea('lulu_client_secret')}
          onSaved={reload}
        />
        <SecretCard
          title="Lulu Webhook Secret"
          description="HMAC secret for verifying Lulu order-status webhooks."
          cfgKey="lulu_webhook_secret"
          envVar="LULU_WEBHOOK_SECRET"
          placeholder="…"
          configured={c('lulu_webhook_secret')}
          envActive={ea('lulu_webhook_secret')}
          onSaved={reload}
        />
        <SecretCard
          title="Lulu Environment"
          description="'sandbox' for testing, 'production' for live orders."
          cfgKey="lulu_env"
          envVar="LULU_ENV"
          placeholder="sandbox | production"
          configured={c('lulu_env')}
          envActive={ea('lulu_env')}
          onSaved={reload}
        />
        <SecretCard
          title="Lulu Public Base URL"
          description="API base URL override (leave blank to use Lulu's default endpoint)."
          cfgKey="lulu_public_base_url"
          envVar="LULU_PUBLIC_BASE_URL"
          placeholder="https://api.lulu.com"
          configured={c('lulu_public_base_url')}
          envActive={ea('lulu_public_base_url')}
          onSaved={reload}
        />
      </Section>

      {/* ── Content & Safety ───────────────────────────────────────── */}
      <Section
        title="Content & Safety"
        description="URL scanning, plagiarism, and content moderation services."
      >
        <SecretCard
          title="Google Safe Browsing Key"
          description="Used to scan outbound URLs in emails before dispatch."
          cfgKey="google_safe_browsing_key"
          envVar="GOOGLE_SAFE_BROWSING_API_KEY"
          placeholder="AIza…"
          configured={c('google_safe_browsing_key')}
          envActive={ea('google_safe_browsing_key')}
          onSaved={reload}
        />
        <SecretCard
          title="Zernio API Key"
          description="Content safety / plagiarism service key."
          cfgKey="zernio_api_key"
          envVar="ZERNIO_API_KEY"
          placeholder="…"
          configured={c('zernio_api_key')}
          envActive={ea('zernio_api_key')}
          onSaved={reload}
        />
        <SecretCard
          title="Zernio Base URL"
          description="Override the default Zernio API endpoint."
          cfgKey="zernio_base_url"
          envVar="ZERNIO_BASE_URL"
          placeholder="https://api.zernio.com"
          configured={c('zernio_base_url')}
          envActive={ea('zernio_base_url')}
          onSaved={reload}
        />
        <SecretCard
          title="Zernio Webhook Secret"
          description="HMAC secret for Zernio callback verification."
          cfgKey="zernio_webhook_secret"
          envVar="ZERNIO_WEBHOOK_SECRET"
          placeholder="…"
          configured={c('zernio_webhook_secret')}
          envActive={ea('zernio_webhook_secret')}
          onSaved={reload}
        />
      </Section>

      {/* ── Integrations ───────────────────────────────────────────── */}
      <Section
        title="Integrations"
        description="Adobe PDF export, Canva cover design, Reddit ads tracking."
      >
        <SecretCard
          title="Adobe Client ID"
          description="Adobe PDF Services OAuth client ID (PDF export)."
          cfgKey="adobe_client_id"
          envVar="ADOBE_CLIENT_ID"
          placeholder="…"
          configured={c('adobe_client_id')}
          envActive={ea('adobe_client_id')}
          onSaved={reload}
        />
        <SecretCard
          title="Adobe Client Secret"
          description="Adobe PDF Services OAuth client secret."
          cfgKey="adobe_client_secret"
          envVar="ADOBE_CLIENT_SECRET"
          placeholder="…"
          configured={c('adobe_client_secret')}
          envActive={ea('adobe_client_secret')}
          onSaved={reload}
        />
        <SecretCard
          title="Reddit Client ID"
          description="Reddit OAuth app client ID (ads conversion tracking)."
          cfgKey="reddit_client_id"
          envVar="REDDIT_CLIENT_ID"
          placeholder="…"
          configured={c('reddit_client_id')}
          envActive={ea('reddit_client_id')}
          onSaved={reload}
        />
        <SecretCard
          title="Reddit Client Secret"
          description="Reddit OAuth app client secret."
          cfgKey="reddit_client_secret"
          envVar="REDDIT_CLIENT_SECRET"
          placeholder="…"
          configured={c('reddit_client_secret')}
          envActive={ea('reddit_client_secret')}
          onSaved={reload}
        />
        <SecretCard
          title="Reddit Pixel ID"
          description="Reddit ads conversion pixel identifier."
          cfgKey="reddit_pixel_id"
          envVar="REDDIT_PIXEL_ID"
          placeholder="…"
          configured={c('reddit_pixel_id')}
          envActive={ea('reddit_pixel_id')}
          onSaved={reload}
        />
      </Section>
    </div>
  );
}
